import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {useEffect, useState} from 'react'
import {QUERY_KEYS} from '@/queryClient.ts'
import {PaginationResponse} from '@/types.ts'
import {httpGetProjects, HttpGetProjectsOptions} from '@/features/projects/http/projects.http'
import {ProjectIndex} from '@/features/projects/types'
import {z} from 'zod'
import {parseAxiosPromise} from '@/axiosInstance'
import {captureException} from '@sentry/react'

const ResponseShape = z.object({data: z.array(ProjectIndex), meta: PaginationResponse})
type ResponseShape = z.infer<typeof ResponseShape>

export const useProjects = (
    params: HttpGetProjectsOptions['params'],
    payload?: HttpGetProjectsOptions['payload'],
    options?: Omit<UseQueryOptions<ResponseShape>, 'queryKey' | 'queryFn'>
) => {
    const [persistedPagination, setPersistedPagination] = useState<PaginationResponse>({
        total: 0,
        lastPage: 1,
        perPage: 50,
        currentPage: 1
    })

    const query = useQuery({
        queryKey: [QUERY_KEYS.PROJECTS, params, payload],
        queryFn: () =>
            parseAxiosPromise({
                axiosPromise: httpGetProjects({params, payload}),
                onZodError: captureException,
                responseShape: ResponseShape
            }),
        ...options
    })

    useEffect(() => {
        if (query.isSuccess) {
            setPersistedPagination(query.data.meta)
        }
    }, [query.data?.data.length])

    return {...query, persistedPagination}
}
